/**
 * Helper compartilhado pra suprimir logs de erro de auth quando o device
 * está em estados pré-aprovação (idle/registering/pending_approval).
 *
 * <p><b>Por quê:</b> Player novo (sem aprovação no admin) tem seu deviceToken
 * mas o backend retorna 401 pra requests autenticadas até aprovar. Sync,
 * Analytics, RemoteOps, OTA disparam suas chamadas em paralelo → todos batem
 * 401 → cada um loga seu próprio erro. Console fica vermelho durante toda a
 * tela "Aguardando aprovação". É barulho — esses 401 são esperados.
 *
 * <p>Cada serviço deve usar {@link logAuthAwareError} no lugar de
 * console.error/warn quando o erro PODE ser causado por device pending. O
 * helper rebaixa pra console.info quando esse for o caso, mantendo warn/error
 * pra erros de verdade (rede caindo, backend 500, etc).
 *
 * <p>Renato 2026-06-12.
 */

/**
 * Snapshot mínimo do estado de auth que o helper precisa pra decidir
 * suprimir logs. Estrutura compatível com {@code authStore} (subset).
 */
export interface AuthStateSnapshot {
  isAuthenticated: boolean;
  registrationStatus: string;
}

/**
 * Provider opcional — quando registrado, helper consulta no momento do log.
 * Quando ausente (testes, bootstrap precoce), helper assume "device pronto"
 * (não suprime nada) — fail-safe pra não esconder erros legítimos.
 *
 * <p>Injetado via {@link setDeviceAuthStateProvider} pelo bootstrap do app
 * (App.tsx ou similar) — evita import circular do {@code authStore} singleton
 * que tem side-effects (instancia stores, instala reactions).
 */
let provider: (() => AuthStateSnapshot) | null = null;

export function setDeviceAuthStateProvider(
  getter: () => AuthStateSnapshot,
): void {
  provider = getter;
}

/** Limpa o provider — útil em testes (afterEach). */
export function resetDeviceAuthStateProvider(): void {
  provider = null;
}

function readState(): AuthStateSnapshot | null {
  if (!provider) return null;
  try {
    return provider();
  } catch {
    return null;
  }
}

/**
 * True quando o device ainda está em pré-aprovação (sem JWT válido). Inclui:
 * - <b>idle</b>: ainda não tentou registrar
 * - <b>registering</b>: enviando registro pro backend
 * - <b>pending_approval</b>: registrado, esperando admin aprovar
 *
 * Quando true, é esperado que QUALQUER chamada autenticada retorne 401.
 * Sem provider configurado, retorna false (fail-safe — não suprime nada).
 */
export function isWaitingDeviceApproval(): boolean {
  const state = readState();
  if (!state) return false;
  if (state.isAuthenticated) return false;
  const s = state.registrationStatus;
  return s === 'idle' || s === 'registering' || s === 'pending_approval';
}

/** Descrição textual do estado atual pra incluir no log. */
export function describeDeviceAuthState(): string {
  const state = readState();
  if (!state) return 'unknown';
  if (state.isAuthenticated) return 'authenticated';
  switch (state.registrationStatus) {
    case 'pending_approval':
      return 'pending admin approval';
    case 'registering':
      return 'registering';
    case 'idle':
      return 'initializing';
    case 'error':
      return 'error';
    default:
      return state.registrationStatus;
  }
}

/**
 * Loga um erro "auth-aware": se o device está em pré-aprovação, rebaixa pra
 * console.info (vira ruído cinza no DevTools, não erro vermelho). Caso
 * contrário, usa o nível especificado (warn/error).
 *
 * @param level severity quando NÃO está pending (warn ou error)
 * @param prefix prefixo do log (ex: '[SyncService]')
 * @param message mensagem
 * @param details detalhes opcionais
 */
export function logAuthAwareError(
  level: 'warn' | 'error',
  prefix: string,
  message: string,
  details?: unknown,
): void {
  if (isWaitingDeviceApproval()) {
    // eslint-disable-next-line no-console
    console.info(
      `${prefix} ⏳ ${message} (device ${describeDeviceAuthState()} — esperado até admin aprovar)`,
      details ?? '',
    );
    return;
  }
  const fn = level === 'error' ? console.error : console.warn;
  fn(`${prefix} ${message}`, details ?? '');
}

// ---------------------------------------------------------------------------
// Recovery — disparo de re-register quando server revogou nossas credenciais.
// Renato 2026-06-12.
// ---------------------------------------------------------------------------

/**
 * Handler que executa a recovery (limpa playerId, força registrationStatus,
 * dispara registerDevice). Injetado pelo {@code stores/index.ts} pra evitar
 * import circular (auth.store → services → auth.store).
 */
let authRecoveryHandler: ((reason: string) => void | Promise<void>) | null =
  null;

export function setAuthRecoveryHandler(
  handler: (reason: string) => void | Promise<void>,
): void {
  authRecoveryHandler = handler;
}

export function resetAuthRecoveryHandler(): void {
  authRecoveryHandler = null;
}

/**
 * Dispara recovery quando um endpoint autenticado retorna 401 inesperado —
 * forte indício de que o server revogou nosso token (player foi deletado no
 * admin / token rotacionado / etc).
 *
 * <p><b>Caller deve checar {@link isWaitingDeviceApproval} ANTES de chamar</b>
 * — 401 em estado pending é esperado e não é evidência de revocation.</p>
 *
 * <p>O cooldown anti-storm fica no próprio handler (não aqui) pra centralizar
 * a política num lugar só. Caller só precisa saber "vi 401 e estou autenticado".</p>
 *
 * @param reason curto contexto pra logs (ex: 'sse-token 401', 'sync 401')
 */
export function triggerAuthRecovery(reason: string): void {
  if (!authRecoveryHandler) {
    // eslint-disable-next-line no-console
    console.warn(
      '[AuthRecovery] handler não configurado — 401 detectado mas sem ação:',
      reason,
    );
    return;
  }
  try {
    void authRecoveryHandler(reason);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[AuthRecovery] handler lançou:', err);
  }
}
